import { useCallback, useEffect, useMemo, useState } from 'react';

import { EMPTY_OPTION_INPUTS, createEmptyForecastSession, packageKey } from './forecastingModel';
import type { ForecastOptionInputState, ForecastSessionState } from './types';

// Inputs only. Totals derive in the tab, so a stored copy can never drift from the live pricing.
export function useForecastingState(offerId: string | undefined) {
  const [session, setSession] = useState<ForecastSessionState>(createEmptyForecastSession);

  useEffect(() => {
    setSession(createEmptyForecastSession());
  }, [offerId]);

  const updateOption = useCallback(
    (optionIndex: number, updater: (option: ForecastOptionInputState) => ForecastOptionInputState) => {
      setSession((previous) => ({
        optionInputs: {
          ...previous.optionInputs,
          [optionIndex]: updater(previous.optionInputs[optionIndex] ?? EMPTY_OPTION_INPUTS),
        },
      }));
    },
    [],
  );

  const setPackages = useCallback(
    (optionIndex: number, assetId: string, nights: number, value: string) => {
      updateOption(optionIndex, (option) => ({
        ...option,
        packagesByAssetLos: { ...option.packagesByAssetLos, [packageKey(assetId, nights)]: value },
      }));
    },
    [updateOption],
  );

  const setField = useCallback(
    <K extends keyof ForecastOptionInputState>(optionIndex: number, field: K, value: ForecastOptionInputState[K]) => {
      updateOption(optionIndex, (option) => ({ ...option, [field]: value }));
    },
    [updateOption],
  );

  const loadDraft = useCallback((draftSession: ForecastSessionState) => setSession(draftSession), []);

  return useMemo(() => ({ session, setPackages, setField, loadDraft }), [session, setPackages, setField, loadDraft]);
}
